import { Link, NavLink } from 'react-router-dom'
import { cn } from '@/lib/utils'

/**
 * `current` marks the item explicitly, for links that differ only in their
 * query (such as `/guests?list=…`), which route matching can't tell apart.
 * When any item sets it, no item is matched by route.
 */
export type AreaNavItem = { to: string; label: string; current?: boolean }

const itemClass = (active: boolean) =>
  cn(
    'inline-flex min-h-11 items-center border-b-2 px-3 text-sm font-medium',
    active ? 'border-action text-fg-strong' : 'border-transparent text-fg-muted hover:border-line-strong hover:text-fg',
  )

/*
 * AreaNav: the sub-navigation between the pages of one area, under its
 * PageHeader. A labelled <nav> of tab-style links, not ARIA tabs: each item
 * is its own route, so the browser's back, new-tab and link semantics apply.
 * The current page is marked with aria-current="page" and an underline, never
 * colour alone. Items the caller can't open are left out by the caller.
 */
export function AreaNav({ label, items }: { label: string; items: AreaNavItem[] }) {
  const explicit = items.some((item) => item.current !== undefined)
  return (
    <nav aria-label={label} className="border-b border-line">
      <ul className="-mb-px flex flex-wrap gap-x-1">
        {items.map((item) => (
          <li key={item.to}>
            {explicit ? (
              <Link to={item.to} aria-current={item.current ? 'page' : undefined} className={itemClass(Boolean(item.current))}>
                {item.label}
              </Link>
            ) : (
              <NavLink to={item.to} end className={({ isActive }) => itemClass(isActive)}>
                {item.label}
              </NavLink>
            )}
          </li>
        ))}
      </ul>
    </nav>
  )
}
